import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import axios from 'axios';
import { BookOpen, Clock, CheckCircle, Play, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PracticeQuiz = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [numQuestions, setNumQuestions] = useState(5);
  const [loading, setLoading] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchTopics = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/practice/topics`);
      setTopics(data);
    } catch (err) { /* silently fail */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/practice/history`);
      setHistory(data);
    } catch (err) { /* silently fail */ }
  }, []);

  useEffect(() => { fetchTopics(); fetchHistory(); }, [fetchTopics, fetchHistory]);

  const startPractice = async () => {
    if (!selectedSubject || !selectedTopic) { toast.error('Select a subject and topic'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/practice/start`, {
        subject: selectedSubject, topic: selectedTopic, num_questions: numQuestions
      });
      setQuizData(data);
      setCurrentQuestion(0);
      setAnswers([]);
      setSelectedAnswer('');
      setTimeRemaining(data.time_limit);
      setQuizStartTime(Date.now());
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'FREE_LIMIT_REACHED') {
        toast.error(detail.message || 'Free limit reached. Upgrade to Pro for unlimited access.');
        setTimeout(() => navigate('/upgrade'), 800);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to start practice quiz');
      }
    } finally { setLoading(false); }
  };

  const submitPractice = useCallback(async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/practice/${quizData.practice_id}/submit`, { answers: finalAnswers });
      toast.success(`Practice complete! Score: ${data.score}/${data.total}`);
      navigate(`/quiz-results/practice`, { state: { ...data, isPractice: true } });
    } catch (err) { toast.error('Failed to submit'); } finally { setSubmitting(false); }
  }, [quizData, navigate]);

  const handleAutoSubmit = useCallback(() => {
    let final = [...answers];
    if (selectedAnswer) final.push({ answer: selectedAnswer.split(')')[0].trim(), time_taken: 0 });
    while (final.length < (quizData?.questions?.length || 0)) final.push({ answer: '', time_taken: 0 });
    submitPractice(final);
  }, [answers, selectedAnswer, quizData, submitPractice]);

  useEffect(() => {
    if (!quizData || submitting || !quizStartTime) return;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - quizStartTime) / 1000);
      const remaining = quizData.time_limit - elapsed;
      setTimeRemaining(remaining);
      if (remaining <= 0) { clearInterval(timer); handleAutoSubmit(); }
    }, 1000);
    return () => clearInterval(timer);
  }, [quizData, submitting, quizStartTime, handleAutoSubmit]);

  const handleNext = () => {
    if (!selectedAnswer) { toast.error('Select an answer'); return; }
    const letter = selectedAnswer.split(')')[0].trim();
    const newAnswers = [...answers, { answer: letter, time_taken: 0 }];
    setAnswers(newAnswers);
    if (currentQuestion < quizData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer('');
    } else { submitPractice(newAnswers); }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const currentSubjectTopics = topics.find(t => t.subject === selectedSubject)?.topics || [];

  if (quizData && !submitting) {
    const question = quizData.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / quizData.questions.length) * 100;
    return (
      <div className="min-h-screen" data-testid="practice-quiz-active">
        <Header />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="mb-6 flex items-center justify-between bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-500" />
              <span className="text-sm font-bold text-purple-600">Practice Mode</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className={`w-5 h-5 ${timeRemaining <= 30 ? 'text-red-500' : 'text-[#6B7280]'}`} />
              <span className={`text-xl font-black ${timeRemaining <= 30 ? 'text-red-500' : 'text-[#111827]'}`} style={{ fontFamily: 'Fredoka, sans-serif' }}>{formatTime(timeRemaining)}</span>
            </div>
          </div>
          <div className="mb-6">
            <div className="flex justify-between text-sm font-bold text-[#6B7280] mb-1">
              <span>Question {currentQuestion + 1}/{quizData.questions.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-purple-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 mb-6 shadow-sm" data-testid="practice-question">
            <p className="text-xs text-[#6B7280] mb-1">{quizData.subject} — {quizData.topic}</p>
            <h3 className="text-lg sm:text-xl font-bold text-[#111827] mb-6">{question?.question}</h3>
            <div className="space-y-3">
              {question?.options.map((opt, idx) => {
                const letter = opt.split(')')[0].trim();
                const isSelected = selectedAnswer === letter;
                return (
                  <button key={idx} onClick={() => setSelectedAnswer(letter)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-purple-500 bg-purple-50' : 'border-slate-100 hover:border-purple-300'}`}
                    data-testid={`practice-option-${letter}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-purple-500 bg-purple-500' : 'border-slate-200'}`}>
                        {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <span className="text-sm font-medium text-[#111827]">{opt}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={handleNext}
            className="w-full px-6 py-4 bg-purple-600 text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(107,33,168)] active:translate-y-1 active:shadow-none transition-all text-lg"
            data-testid="practice-next-button">
            {currentQuestion < quizData.questions.length - 1 ? 'Next Question' : 'Submit Practice'}
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="practice-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>Practice Quiz</h1>
          <p className="text-[#6B7280]">Free practice with timer. No entry fee, no prizes — just learning!</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 shadow-sm mb-8">
          <h3 className="font-bold text-[#111827] mb-4">Choose your topic</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {topics.map(t => (
              <button key={t.id} onClick={() => { setSelectedSubject(t.subject); setSelectedTopic(''); }}
                className={`p-3 rounded-xl border-2 text-sm font-bold transition-all text-center ${selectedSubject === t.subject ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-100 text-[#6B7280] hover:border-purple-300'}`}
                data-testid={`subject-${t.id}`}>
                {t.subject}
                <p className="text-xs font-normal mt-0.5 opacity-70">{t.difficulty}</p>
              </button>
            ))}
          </div>
          {selectedSubject && (
            <div className="mb-6">
              <p className="text-sm font-bold text-[#111827] mb-2">Select Topic</p>
              <div className="flex flex-wrap gap-2">
                {currentSubjectTopics.map(tp => (
                  <button key={tp} onClick={() => setSelectedTopic(tp)}
                    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${selectedTopic === tp ? 'bg-purple-600 text-white' : 'bg-slate-100 text-[#6B7280] hover:bg-purple-100'}`}
                    data-testid={`topic-${tp.replace(/\s+/g, '-').toLowerCase()}`}>{tp}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm font-bold text-[#111827]">Questions:</span>
            {[5, 10].map(n => (
              <button key={n} onClick={() => setNumQuestions(n)}
                className={`px-4 py-2 rounded-xl text-sm font-bold ${numQuestions === n ? 'bg-purple-600 text-white' : 'bg-slate-100 text-[#6B7280]'}`}
                data-testid={`num-q-${n}`}>{n}</button>
            ))}
          </div>
          <button onClick={startPractice} disabled={loading || !selectedSubject || !selectedTopic}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-purple-600 text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(107,33,168)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 text-lg"
            data-testid="start-practice-button">
            {loading ? (<><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> Generating...</>) : (<><Play className="w-5 h-5" /> Start Practice</>)}
          </button>
        </div>
        {history.length > 0 && (
          <div>
            <h3 className="font-bold text-[#111827] mb-4">Recent Practice</h3>
            <div className="space-y-3">
              {history.slice(0, 5).map((h, idx) => (
                <div key={idx} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between" data-testid={`practice-history-${idx}`}>
                  <div>
                    <p className="font-bold text-[#111827] text-sm">{h.subject} — {h.topic}</p>
                    <p className="text-xs text-[#6B7280]">{new Date(h.completed_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-[#0EA5E9]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{h.score}/{h.total}</p>
                    <p className="text-xs text-[#6B7280]">{h.time_taken}s</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PracticeQuiz;
